// eslint-disable-next-line
import CustomScriptWorker from 'worker-loader!@/workers/custom-script-worker.js';
import { useStt } from '@ht/lib-plural-vue';
import types from '@/actions/types';
import useSequence from '@/composables/useSequence';
import useConfig from '@/composables/useConfig';
import { SYSTEM_VARIABLES, SYSTEM_VARIABLES_MAP, CUSTOM_SCRIPT_TIMEOUT } from '@/js/constants';
import { onSttTextCb } from '@/js/stt';
import Base from '@/actions/base';
import useAnalytics from '@/composables/useAnalytics';
import ChangeLanguage from '@/actions/changeLanguage';

export default class CustomScript extends Base {
  constructor(params) {
    super(params, types.CUSTOM_SCRIPT);

    this.stopped = false;
    this.worker = null;
    this.timeout = null;
    this.paused = false;
    this.shouldComplete = null;
  }

  execute() {
    const { runSequence } = useSequence();
    const {
      initialConfig,
      sharedConfig,
      currentConfig,
      getVariable,
      setVariables,
      setLanguageActive,
      isPreview,
      setShowLanguageSwitch,
      setFlowStateBackToStart,
    } = useConfig();
    const { disableStt, initSTT } = useStt();
    const { trackAction } = useAnalytics();
    trackAction(this);

    this.worker = new CustomScriptWorker();
    this.worker.onmessage = async (e) => {
      const { action, payload } = e.data;
      switch (action) {
        case 'switch_language': {
          const lang = currentConfig.value.contentLanguages.find((l) => l.iso639_1 === payload.lang)?.code;
          const changeLanguage = new ChangeLanguage({ value: lang });
          await changeLanguage.execute();
          break;
        }
        case 'activate_language': {
          setLanguageActive(payload.lang, payload.flag);
          break;
        }
        case 'show_language_switch': {
          setShowLanguageSwitch(payload.flag);
          break;
        }
        case 'set_variable': {
          // only set if there is an id and its not a system variable
          if (payload?.id && !SYSTEM_VARIABLES.some((v) => v.id === payload.id)) {
            setVariables([{
              id: payload.id,
              value: { value: JSON.parse(payload.value) },
            }]);
          }
          break;
        }
        case 'open_link': {
          const { url, target, windowFeatures } = payload;
          window.open(url, target, windowFeatures);
          break;
        }
        case 'enable_stt': {
          if (currentConfig.value.speechServicesActive) {
            if (!payload.flag) {
              disableStt();
            } else {
              const postMessage = {
                type: 'resume',
                payload: {},
              };
              try {
                await initSTT(currentConfig.value, onSttTextCb, {
                  projectId: initialConfig.value.projectId,
                  sessionId: getVariable(SYSTEM_VARIABLES_MAP.SESSION_ID.id).value,
                  isPreview: isPreview(),
                });
              } catch (error) {
                postMessage.payload = { error };
              }

              this.worker.postMessage(postMessage);
            }
          } else if (payload.flag) {
            this.worker.postMessage({
              type: 'resume',
              payload: {},
            });
          }
          break;
        }
        case 'error': {
          if (!this.stopped) {
            this.stopTimeout();
            if (!this.paused) {
              this.resolve(payload);
              if (this.params.failureFlow) {
                setFlowStateBackToStart(this.params.failureFlow);
                runSequence(this.params.failureFlow);
              }
            } else {
              this.shouldComplete = {
                result: payload,
                next: this.params.failureFlow,
              };
            }
          }
          break;
        }
        case 'finished': {
          if (!this.stopped) {
            this.stopTimeout();
            if (!this.paused) {
              this.resolve();
              if (this.params.successFlow) {
                setFlowStateBackToStart(this.params.successFlow);
                runSequence(this.params.successFlow);
              }
            } else {
              this.shouldComplete = {
                result: null,
                next: this.params.successFlow,
              };
            }
          }
          break;
        }
        default: {
          console.log('unsupported action'); // eslint-disable-line no-console
        }
      }
    };

    return Promise.race([
      new Promise((resolve) => {
        this.worker.postMessage({
          type: 'start',
          payload: {
            context: {
              robotId: currentConfig.value.robotId,
              robotName: currentConfig.value.robotName,
            },
            variables: JSON.stringify(sharedConfig.value.variables.map((v) => ({
              id: v.id,
              name: v.name,
              value: typeof v.value === 'function' ? v.value() : v.value,
              isSystemVariable: SYSTEM_VARIABLES.some((vs) => vs.id === v.id),
            }))),
            code: this.params.code,
          },
        });

        this.resolve = resolve;
      }),
      new Promise((resolve) => {
        this.timeout = setTimeout(() => {
          this.worker?.terminate();
          this.worker = null;
          if (!this.paused) {
            resolve('timeout');
            if (this.params.failureFlow) {
              setFlowStateBackToStart(this.params.failureFlow);
              runSequence(this.params.failureFlow);
            }
          } else if (!this.shouldComplete) {
            this.shouldComplete = {
              result: 'timeout',
              next: this.params.failureFlow,
            };
          }
        }, CUSTOM_SCRIPT_TIMEOUT);
      }),
    ]);
  }

  stopTimeout() {
    if (this.timeout) {
      clearTimeout(this.timeout);
      this.timeout = null;
    }
  }

  stop() {
    this.stopTimeout();
    this.worker?.terminate();
    this.worker = null;
    this.stopped = true;
    this.resolve();
  }

  pause() {
    this.paused = true;
  }

  resume() {
    this.paused = false;
    if (this.shouldComplete) {
      this.resolve(this.shouldComplete.result);
      if (this.shouldComplete.next) {
        const { runSequence } = useSequence();
        const { setFlowStateBackToStart } = useConfig();

        setFlowStateBackToStart(this.shouldComplete.next);
        runSequence(this.shouldComplete.next);
      }
    }
  }
}
